<script setup lang="ts">
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem
} from '@/components/ui/carousel';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import type { GenericImage } from '.';
import ZoomableControl from '~/components/ui/zoomable-image/ZoomableControl.vue';
import ZoomableImage from '~/components/ui/zoomable-image/ZoomableImage.vue';
import ThumbnailNavigationWrapper from './ThumbnailNavigationWrapper.vue';
import ThumbnailActionsWrapper from './ThumbnailActionsWrapper.vue';
import FloatingButton from '~/components/ui/floating-button/FloatingButton.vue';
import { cn } from '~/utils';

const props = defineProps<{
	images: GenericImage[];
	altCallback: (index: number) => string;
}>();

const emblaMainApi = ref<CarouselApi>();
const emblaThumbnailApi = ref<CarouselApi>();
const emblaSelectorsApi = ref<CarouselApi>();
const emblaSelectorsZoomableApi = ref<CarouselApi>();
const selectedIndex = defineModel<number>('selectedIndex', {
	required: true,
	default: 0
});

const isZoomed = ref(false);
const selectedImage = ref<GenericImage | null>(null);
const zoomCoords = ref({ x: 0, y: 0 });

const handleZoomImage = (zoom: boolean) => {
	isZoomed.value = zoom;
};

const emits = defineEmits<{
	(event: 'update-zoom', zoom: boolean): void;
}>();

watch(
	() => isZoomed.value,
	() => {
		emits('update-zoom', isZoomed.value);
	}
);

watch(
	() => selectedIndex.value,
	() => {
		handleZoomImage(false);
	}
);

function onSelect() {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
	emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap());
	emblaSelectorsApi.value?.scrollTo(emblaMainApi.value.selectedScrollSnap());
}

async function closeZoomAndThumbClick(index: number) {
	await closeZoom();
	onThumbClick(index);
}

function onThumbClick(index: number) {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	emblaMainApi.value.scrollTo(index);
}

async function scrollTo(index: number, jump: boolean = false) {
	if (!emblaMainApi.value) return;

	emblaMainApi.value.scrollTo(index, jump);
	emblaThumbnailApi.value?.scrollTo(index, jump);
	emblaSelectorsApi.value?.scrollTo(index, jump);
}

function setupCarouselApi(api: CarouselApi) {
	emblaMainApi.value = api;

	emblaMainApi.value?.on('select', onSelect);
	emblaMainApi.value?.on('reInit', onSelect);

	scrollTo(selectedIndex.value);
}

function setupThumbnailCarouselApi(api: CarouselApi) {
	emblaThumbnailApi.value = api;
}

function setupSelectorsCarouselApi(api: CarouselApi) {
	emblaSelectorsApi.value = api;
}

function setupSelectorsCarouselZoomableApi(api: CarouselApi) {
	if (!emblaMainApi.value) return;
	emblaSelectorsZoomableApi.value = api;

	emblaSelectorsZoomableApi?.value?.scrollTo(
		emblaMainApi.value.selectedScrollSnap()
	);
}

const isFirstItem = computed(() => selectedIndex.value === 0);
const isLastItem = computed(
	() => selectedIndex.value === props.images.length - 1
);

async function handleKeydown(event: KeyboardEvent) {
	if (isZoomed.value) {
		await closeZoom();
	}

	if (event.key === 'ArrowLeft') {
		scrollTo(selectedIndex.value - 1);
	} else if (event.key === 'ArrowRight') {
		scrollTo(selectedIndex.value + 1);
	}
}

onKeyStroke(['ArrowRight', 'ArrowLeft'], event => handleKeydown(event));
function openZoom(image: GenericImage, coords: { x: number; y: number }) {
	selectedImage.value = image;
	isZoomed.value = true;
	zoomCoords.value = coords;
}

async function closeZoom() {
	selectedImage.value = null;
	isZoomed.value = false;
	zoomCoords.value = { x: 0, y: 0 };

	await until(() => emblaMainApi.value).toBeTruthy();
	scrollTo(selectedIndex.value, true);

	// Wait for the carousel to finish scrolling
	await new Promise(resolve => setTimeout(resolve, 25));
}

const containerZoomedClass = computed(() =>
	isZoomed.value ? 'flex items-start justify-center' : ''
);
</script>
<template>
	<div :class="cn('h-full', containerZoomedClass)">
		<section
			v-if="selectedImage"
			class="flex h-[90vh] max-h-[95%] w-full flex-col items-start md:h-[95%] md:max-h-none md:items-center md:justify-center"
		>
			<ZoomableImage
				:image-props="{
					src: selectedImage.watermarkUrl || selectedImage.url || ''
				}"
				:zoom-position="zoomCoords"
				@close="closeZoom"
			/>

			<ThumbnailNavigationWrapper
				class-name="mx-auto mt-5 flex h-5 items-center justify-center focus:outline-none focus:ring-0 md:w-[30%]"
				:images="images"
				:selected-index="selectedIndex"
				@init-api="setupSelectorsCarouselZoomableApi"
				@thumb-click="closeZoomAndThumbClick"
			/>

			<ThumbnailActionsWrapper
				:is-first-item="isFirstItem"
				:is-last-item="isLastItem"
				:on-prev="() => closeZoomAndThumbClick(selectedIndex - 1)"
				:on-next="() => closeZoomAndThumbClick(selectedIndex + 1)"
				position="zoom"
				@prev="() => closeZoomAndThumbClick(selectedIndex - 1)"
				@next="() => closeZoomAndThumbClick(selectedIndex + 1)"
			/>
		</section>

		<Container
			v-else
			main-class="h-full relative"
			class="relative h-full md:pb-10 md:pt-6"
		>
			<Carousel
				class="relative mx-auto flex h-full flex-col md:w-2/3"
				:is-zoomed="isZoomed"
				:plugins="[WheelGesturesPlugin()]"
				@init-api="setupCarouselApi"
			>
				<CarouselContent class="size-full gap-4">
					<CarouselItem
						v-for="(image, index) in images"
						:key="`principal-thumbnail-carousel-item: ${index}`"
						class="flex h-full justify-center"
					>
						<ZoomableControl @zoom-in="zoomPoint => openZoom(image, zoomPoint)">
							<FloatingButton
								container-class="absolute left-1/2 bottom-8 transform -translate-x-1/2"
								@click="openZoom(image, { x: 50, y: 50 })"
							>
								<IconMaterialSymbolsLightZoomInRounded class="size-6" />
							</FloatingButton>
							<NuxtImg
								:src="image.watermarkUrl || image.url"
								class="size-full object-cover md:object-contain"
								provider="AWSServerlessImageHandler"
								:placeholder="[50, 25]"
								:alt="altCallback(index)"
							/>
						</ZoomableControl>
					</CarouselItem>
				</CarouselContent>
			</Carousel>

			<ThumbnailNavigationWrapper
				class="mx-auto mt-5 flex h-5 items-center justify-center focus:outline-none focus:ring-0 md:w-[30%]"
				:images="images"
				:selected-index="selectedIndex"
				@init-api="setupSelectorsCarouselApi"
				@thumb-click="onThumbClick"
			/>

			<ThumbnailActionsWrapper
				:is-first-item="isFirstItem"
				:is-last-item="isLastItem"
				position="main"
				@prev="() => scrollTo(selectedIndex - 1)"
				@next="() => scrollTo(selectedIndex + 1)"
			/>
		</Container>

		<Container
			v-if="!selectedImage"
			class="h-[140px] border-t border-white-dark-3 py-6 md:py-6"
			main-class="relative"
		>
			<Carousel
				class="mx-auto flex justify-center focus:outline-none focus:ring-0 md:w-[90%]"
				@init-api="setupThumbnailCarouselApi"
			>
				<CarouselContent class="gap-4">
					<CarouselItem
						v-for="(image, index) in images"
						:key="`secondary-thumbnail-carousel-item: ${index}`"
						class="aspect-square h-[88px] w-[132px] basis-[132px] cursor-pointer gap-4"
						@click="onThumbClick(index)"
					>
						<NuxtImg
							:src="image.watermarkUrl || image.url"
							provider="AWSServerlessImageHandler"
							:placeholder="[50, 25]"
							class="aspect-square size-full object-cover"
							:class="{ 'border border-primary': index === selectedIndex }"
							:alt="altCallback(index)"
						/>
					</CarouselItem>
				</CarouselContent>
			</Carousel>

			<ThumbnailActionsWrapper
				:is-first-item="isFirstItem"
				:is-last-item="isLastItem"
				position="thumbnail"
				@prev="() => scrollTo(selectedIndex - 1)"
				@next="() => scrollTo(selectedIndex + 1)"
			/>
		</Container>
	</div>
</template>
